import { type JSX, useCallback, useEffect, useState } from 'react';

import {
  REGELNAME,
  abrechnungsgrundlage as ladeGrundlage,
  eingangsrechnungPruefen,
  type Abrechnungsgrundlage as Grundlage,
  type Pruefergebnis,
} from '../api/honorare';
import { alsEurosatz } from '../betrag';
import { ApiFehler } from '../api/fehler';
import { Meldung } from '../komponenten/Meldung';
import { Abschnitt, Feldraster } from '../komponenten/Akte';

/**
 * Die Abrechnungsgrundlage eines externen Trainers (RES-76, RES-77, RES-62).
 *
 * ============================================================================
 * WOZU SIE DA IST
 * ============================================================================
 *
 * Der Dienstleister stellt seine eigene Rechnung (E-60). Diese Karte ist das,
 * wogegen die Buchhaltung sie prüft: je durchgeführtem Einsatz das vereinbarte
 * Honorar, was nach der Rückmeldung davon abzurechnen ist, und ob der Einsatz
 * schon mit einer Rechnung abgerechnet wurde.
 *
 * ZWEI SCHRITTE BEIM PRÜFEN. Erst der Vergleich — Grundlage gegen
 * Rechnungsbetrag —, dann der Vermerk. Weicht der Betrag ab, verlangt der
 * Vermerk einen Grund; eine Abweichung ohne Grund ist in einem Jahr nicht mehr
 * zu erklären.
 */

function monatsanfang(): string {
  const d = new Date();
  return `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function monatsende(): string {
  const d = new Date();
  const ende = new Date(Date.UTC(d.getFullYear(), d.getMonth() + 1, 0));
  return ende.toISOString().slice(0, 10);
}

function datum(wert: string): string {
  return new Date(wert).toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Europe/Berlin',
  });
}

export function Abrechnungsgrundlage({
  ressource,
  darfPruefen,
}: {
  readonly ressource: string;
  /** `rechnung:buchen` — die Buchhaltung prüft die Eingangsrechnung. */
  readonly darfPruefen: boolean;
}): JSX.Element {
  const [von, setzeVon] = useState(monatsanfang);
  const [bis, setzeBis] = useState(monatsende);
  const [grundlage, setzeGrundlage] = useState<Grundlage | null>(null);
  const [gewaehlt, setzeGewaehlt] = useState<ReadonlySet<string>>(new Set());
  const [rechnung, setzeRechnung] = useState('');
  const [betrag, setzeBetrag] = useState('');
  const [bemerkung, setzeBemerkung] = useState('');
  const [vergleich, setzeVergleich] = useState<Pruefergebnis | null>(null);
  const [fehler, setzeFehler] = useState<ApiFehler | null>(null);
  const [meldung, setzeMeldung] = useState<string | null>(null);
  const [laeuft, setzeLaeuft] = useState(false);

  const holen = useCallback(async () => {
    try {
      setzeGrundlage(await ladeGrundlage(ressource, von, bis));
      setzeFehler(null);
    } catch (f) {
      setzeFehler(f instanceof ApiFehler ? f : null);
      if (!(f instanceof ApiFehler)) throw f;
    }
  }, [ressource, von, bis]);

  useEffect(() => {
    void holen();
  }, [holen]);

  const pruefe = useCallback(
    async (bestaetigen: boolean) => {
      setzeLaeuft(true);
      setzeFehler(null);
      setzeMeldung(null);
      try {
        const ergebnis = await eingangsrechnungPruefen({
          ressource,
          von,
          bis,
          belegungen: [...gewaehlt],
          eingangsrechnung: rechnung,
          rechnungsbetrag: betrag,
          bemerkung: bemerkung.trim() === '' ? null : bemerkung,
          bestaetigen,
        });
        setzeVergleich(ergebnis);
        if (ergebnis.vermerkt) {
          setzeMeldung(
            `${String(gewaehlt.size)} Einsätze mit Rechnung ${rechnung} als abgerechnet vermerkt.`,
          );
          setzeGewaehlt(new Set());
          setzeVergleich(null);
          await holen();
        }
      } catch (f) {
        setzeFehler(f instanceof ApiFehler ? f : null);
        if (!(f instanceof ApiFehler)) throw f;
      } finally {
        setzeLaeuft(false);
      }
    },
    [ressource, von, bis, gewaehlt, rechnung, betrag, bemerkung, holen],
  );

  const offene = (grundlage?.einsaetze ?? []).filter((e) => e.eingangsrechnung === null);

  return (
    <Abschnitt titel="Abrechnungsgrundlage">
      <p className="hilfe">
        Die durchgeführten Einsätze mit dem vereinbarten Honorar und dem, was nach der
        Rückmeldung abzurechnen ist — die Grundlage, gegen die die Rechnung des Trainers
        geprüft wird.
      </p>

      {fehler !== null && <Meldung fehler={fehler} />}
      {meldung !== null && (
        <p className="hinweiszeile" role="status">
          {meldung}
        </p>
      )}

      <Feldraster>
        <label className="feld">
          <span className="feld__beschriftung">Von</span>
          <input
            type="date"
            value={von}
            onChange={(e) => {
              setzeVon(e.target.value);
            }}
          />
        </label>
        <label className="feld">
          <span className="feld__beschriftung">Bis</span>
          <input
            type="date"
            value={bis}
            onChange={(e) => {
              setzeBis(e.target.value);
            }}
          />
        </label>
      </Feldraster>

      {grundlage !== null && grundlage.einsaetze.length === 0 && (
        <p className="liste__leer">Im Zeitraum gibt es keinen abzurechnenden Einsatz.</p>
      )}

      {grundlage !== null && grundlage.einsaetze.length > 0 && (
        <>
          <p className="hinweiszeile">
            Abzurechnen {alsEurosatz(grundlage.summen.abzurechnen)} (beauftragt{' '}
            {alsEurosatz(grundlage.summen.beauftragt)}) · offen{' '}
            <strong>{alsEurosatz(grundlage.summen.offen)}</strong> · abgerechnet{' '}
            {alsEurosatz(grundlage.summen.abgerechnet)}
          </p>
          <div className="tabellenhuelle tabellenhuelle--telefon">
            <table className="liste__tabelle" aria-label="Einsätze der Abrechnungsgrundlage">
              <thead>
                <tr>
                  {darfPruefen && <th scope="col">Wahl</th>}
                  <th scope="col">Einsatz</th>
                  <th scope="col">Leistung</th>
                  <th scope="col">Beauftragt</th>
                  <th scope="col">Abzurechnen</th>
                  <th scope="col">Regel</th>
                  <th scope="col">Abgerechnet</th>
                </tr>
              </thead>
              <tbody>
                {grundlage.einsaetze.map((e) => (
                  <tr key={e.belegung} className={e.eingangsrechnung === null ? undefined : 'liste__zeile--blass'}>
                    {darfPruefen && (
                      <td>
                        {e.eingangsrechnung === null && (
                          <input
                            type="checkbox"
                            aria-label={`Einsatz am ${datum(e.beginn)} wählen`}
                            checked={gewaehlt.has(e.belegung)}
                            onChange={() => {
                              setzeGewaehlt((alt) => {
                                const neu = new Set(alt);
                                if (neu.has(e.belegung)) neu.delete(e.belegung);
                                else neu.add(e.belegung);
                                return neu;
                              });
                              setzeVergleich(null);
                            }}
                          />
                        )}
                      </td>
                    )}
                    <th scope="row">
                      {datum(e.beginn)}
                      {e.produkt !== null && (
                        <>
                          <br />
                          <span className="hilfe">{e.produkt}</span>
                        </>
                      )}
                    </th>
                    <td>{e.leistung ?? '—'}</td>
                    <td>{alsEurosatz(e.beauftragt)}</td>
                    <td>
                      <strong>{alsEurosatz(e.abzurechnen)}</strong>
                    </td>
                    <td>
                      <span
                        className={
                          e.regel === 'von_hand_pruefen' ? 'marke marke--warn' : 'marke'
                        }
                      >
                        {REGELNAME[e.regel] ?? e.regel}
                      </span>
                    </td>
                    <td>{e.eingangsrechnung ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {darfPruefen && offene.length > 0 && (
            <section aria-label="Eingangsrechnung prüfen">
              <h3 className="karte__unterteil">Eingangsrechnung prüfen</h3>
              <Feldraster>
                <label className="feld">
                  <span className="feld__beschriftung">Rechnungsnummer des Trainers</span>
                  <input
                    value={rechnung}
                    onChange={(e) => {
                      setzeRechnung(e.target.value);
                      setzeVergleich(null);
                    }}
                  />
                </label>
                <label className="feld">
                  <span className="feld__beschriftung">Rechnungsbetrag (€)</span>
                  <input
                    inputMode="decimal"
                    value={betrag}
                    onChange={(e) => {
                      setzeBetrag(e.target.value);
                      setzeVergleich(null);
                    }}
                  />
                </label>
              </Feldraster>
              <div className="knopfreihe">
                <button
                  type="button"
                  className="knopf knopf--klein"
                  disabled={laeuft || gewaehlt.size === 0 || rechnung.trim() === '' || betrag.trim() === ''}
                  onClick={() => void pruefe(false)}
                >
                  Vergleichen
                </button>
              </div>

              {vergleich !== null && (
                <>
                  <p className="hinweiszeile" role="status">
                    Grundlage {alsEurosatz(vergleich.grundlage)} · Rechnung{' '}
                    {alsEurosatz(vergleich.rechnungsbetrag)} ·{' '}
                    {vergleich.differenz === '0.00' ? (
                      <span className="marke marke--gut">stimmt überein</span>
                    ) : (
                      <span className="marke marke--warn">
                        Differenz {alsEurosatz(vergleich.differenz.replace('-', ''))}
                        {vergleich.differenz.startsWith('-') ? ' weniger' : ' mehr'}
                      </span>
                    )}
                  </p>
                  {vergleich.differenz !== '0.00' && (
                    <label className="feld">
                      <span className="feld__beschriftung">Warum die Abweichung stimmt</span>
                      <textarea
                        value={bemerkung}
                        onChange={(e) => {
                          setzeBemerkung(e.target.value);
                        }}
                      />
                    </label>
                  )}
                  <div className="knopfreihe">
                    <button
                      type="button"
                      className="knopf knopf--haupt knopf--klein"
                      disabled={laeuft}
                      onClick={() => void pruefe(true)}
                    >
                      Als abgerechnet vermerken
                    </button>
                  </div>
                </>
              )}
            </section>
          )}
        </>
      )}
    </Abschnitt>
  );
}
